import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { Calendar as CalendarIcon, AlertCircle, Clock, MapPin, Check, X, CalendarClock, Video, List, LayoutGrid, ShieldCheck, Camera, CheckSquare } from 'lucide-react';
import { useAuth } from '../../context/AuthContext';
import { api, ApiError } from '../../lib/api';
import CalendarView from '../../components/media/MediaCalendar/MediaCalendar';
import MediaEventDetail from '../../components/media/MediaCalendar/MediaEventDetail';
import RescheduleDecisionDialog from '../../components/admin/RescheduleDecisionDialog';
import PreMeetingScheduler from '../../components/media/PreMeetingScheduler';
import RescheduleEventDialog from '../../components/portal/RescheduleEventDialog';
import CompleteAndDeliverForm from '../../components/media/CompleteAndDeliverForm';
import {
  eventStatusBadgeClass,
  eventStatusLabel,
  noticeTierBadgeClass,
  noticeTierLabel,
  formatMediaDateRange,
} from '../../lib/media';

// /media — Media-role default landing.
//
// Three sections stacked:
//   1. Needs-action banner — pending invitations within the 24h window
//   2. Upcoming events     — confirmed (or pending/shooting/editing) and not yet past
//   3. Recent events       — last 5 completed/cancelled
//
// The own-only contract is enforced server-side (/api/media/me/events) —
// no client-side filtering by user id, no client-side trust.

export default function MediaDashboard() {
  const { user } = useAuth();
  const [upcoming, setUpcoming] = useState([]);
  const [needsAction, setNeedsAction] = useState([]);
  const [approvalQueue, setApprovalQueue] = useState([]);
  const [recent, setRecent] = useState([]);
  const [rescheduleRequests, setRescheduleRequests] = useState([]);
  const [projectShoots, setProjectShoots] = useState([]); // website-project shoots held for me
  const [myTasks, setMyTasks] = useState([]);
  const [decideTarget, setDecideTarget] = useState(null);
  const [premeetingTarget, setPremeetingTarget] = useState(null);
  const [rescheduleTarget, setRescheduleTarget] = useState(null);
  const [completeTarget, setCompleteTarget] = useState(null);
  const [oauthConnected, setOauthConnected] = useState(false);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [calView, setCalView] = useState('list'); // 'list' | 'calendar'
  const [detailTarget, setDetailTarget] = useState(null);

  const load = useCallback(async () => {
    setLoading(true);
    setError('');
    try {
      const [up, need, rec, reqs, av, shoots, tasks] = await Promise.all([
        api.get('/api/media/me/events?upcoming=1'),
        api.get('/api/media/me/events?needs_action=1'),
        api.get('/api/media/me/events?status=delivered'),
        api.get('/api/media/me/reschedule-requests'),
        api.get('/api/media/me/availability'),
        api.get('/api/media/me/project-shoots').catch(() => ({ shoots: [] })),
        api.get('/api/media/me/tasks').catch(() => ({ tasks: [] })),
      ]);
      setProjectShoots((shoots?.shoots || []).filter((s) => !s.done));
      setMyTasks(tasks?.tasks || []);
      const isApprovalItem = (e) => e.approvalState === 'pending' && !e.assignedUserName && !e.externalFallbackName;
      setUpcoming((up?.events || []).filter((e) => !isApprovalItem(e)));
      setApprovalQueue((need?.events || []).filter(isApprovalItem));
      setNeedsAction((need?.events || []).filter((e) => !isApprovalItem(e)));
      setRescheduleRequests(reqs?.requests || []);
      setOauthConnected(av?.googleOauthStatus === 'connected');
      // Most-recent-first; cap at 5
      setRecent((rec?.events || []).slice().reverse().slice(0, 5));
    } catch (err) {
      setError(err?.message || 'Failed to load events');
    } finally {
      setLoading(false);
    }
  }, []);
  useEffect(() => { load(); }, [load]);

  // Group upcoming by ISO date (NYC local) for the day-grouped list view.
  const upcomingByDay = useMemo(() => groupByLocalDay(upcoming), [upcoming]);

  const confirm = useCallback(async (eventId) => {
    try {
      await api.post(`/api/media/me/events/${eventId}/confirm`);
      load();
    } catch (err) {
      setError(err instanceof ApiError ? err.message : 'Failed to confirm');
    }
  }, [load]);

  const approve = useCallback(async (eventId) => {
    try {
      await api.post(`/api/media/me/events/${eventId}/approve`);
      load();
    } catch (err) {
      setError(err instanceof ApiError ? err.message : 'Failed to approve');
    }
  }, [load]);

  const decline = useCallback(async (eventId) => {
    const reason = window.prompt('Optional reason for declining (visible to Ops):') ?? '';
    try {
      await api.post(`/api/media/me/events/${eventId}/decline`, { reason });
      load();
    } catch (err) {
      setError(err instanceof ApiError ? err.message : 'Failed to decline');
    }
  }, [load]);

  const completeTask = useCallback(async (taskId) => {
    try {
      await api.post(`/api/media/me/tasks/${taskId}/complete`, {});
      setMyTasks((prev) => prev.filter((t) => t.id !== taskId));
    } catch (err) {
      setError(err instanceof ApiError ? err.message : 'Failed to complete the task');
    }
  }, []);

  const cancelPremeeting = useCallback(async (eventId) => {
    if (!window.confirm('Cancel the pre-event meeting? This removes the Google Calendar invite.')) return;
    try {
      await api.delete(`/api/media/me/events/${eventId}/premeeting`);
      load();
    } catch (err) {
      setError(err instanceof ApiError ? err.message : 'Failed to cancel meeting');
    }
  }, [load]);

  return (
    <div className="max-w-4xl mx-auto">
      <header className="mb-6">
        <h1 className="text-2xl font-semibold text-white tracking-tight">
          {timeGreeting()}, <span className="serif-acc accent-text">{firstNameOf(user?.name)}</span>
        </h1>
        <p className="text-[13px] text-gray-500 mt-0.5">
          Your event calendar and pending invitations.
        </p>
      </header>

      {error && (
        <div className="mb-4 text-[12px] text-red-300 bg-red-500/10 border border-red-500/20 rounded-lg p-3">
          {error}
        </div>
      )}

      {!loading && rescheduleRequests.length > 0 && (
        <section className="mb-6">
          <div className="rounded-xl border border-[#B0FC38]/30 bg-[#B0FC38]/[0.06] p-4">
            <div className="flex items-start gap-3 mb-3">
              <CalendarClock className="w-4 h-4 text-[#B0FC38] mt-0.5 shrink-0" />
              <div>
                <div className="text-sm text-white font-medium">
                  {rescheduleRequests.length} reschedule request{rescheduleRequests.length === 1 ? '' : 's'}
                </div>
                <div className="text-[12px] text-[#B0FC38]/80 mt-0.5">
                  Schools have asked to move these bookings. Approve or reject so the calendar locks in.
                </div>
              </div>
            </div>
            <div className="space-y-2">
              {rescheduleRequests.map((r) => (
                <button key={r.id} type="button" onClick={() => setDecideTarget(r)}
                  className="block w-full text-left rounded-lg border border-white/[0.05] bg-white/[0.02] hover:bg-white/[0.04] px-3 py-2.5">
                  <div className="text-[13px] text-white truncate">{r.eventTitle}</div>
                  <div className="text-[11px] text-gray-500 mt-0.5">
                    {r.accountName} · {formatMediaDateRange(r.originalScheduledStartAt, r.originalScheduledEndAt)} → {formatMediaDateRange(r.newScheduledStartAt, r.newScheduledEndAt)}
                  </div>
                </button>
              ))}
            </div>
          </div>
        </section>
      )}

      {!loading && approvalQueue.length > 0 && (
        <section className="mb-6">
          <div className="rounded-xl border border-[#B0FC38]/30 bg-[#B0FC38]/[0.05] p-4">
            <div className="flex items-start gap-3 mb-3">
              <ShieldCheck className="w-4 h-4 text-[#B0FC38] mt-0.5 shrink-0" />
              <div>
                <div className="text-sm text-white font-medium">
                  {approvalQueue.length} booking{approvalQueue.length === 1 ? '' : 's'} awaiting approval
                </div>
                <div className="text-[12px] text-gray-400 mt-0.5">
                  New school requests. Approving books it onto your calendar (conflicts are blocked automatically). Ops/Admin can also decide these.
                </div>
              </div>
            </div>
            <div className="space-y-2">
              {approvalQueue.map((ev) => (
                <EventCard
                  key={ev.id}
                  ev={ev}
                  highlight
                  confirmLabel="Approve & book"
                  onConfirm={() => approve(ev.id)}
                />
              ))}
            </div>
          </div>
        </section>
      )}

      {!loading && needsAction.length > 0 && (
        <section className="mb-6">
          <div className="rounded-xl border border-amber-500/30 bg-amber-500/[0.06] p-4">
            <div className="flex items-start gap-3 mb-3">
              <AlertCircle className="w-4 h-4 text-amber-300 mt-0.5 shrink-0" />
              <div>
                <div className="text-sm text-white font-medium">
                  {needsAction.length} pending invitation{needsAction.length === 1 ? '' : 's'}
                </div>
                <div className="text-[12px] text-amber-200/80 mt-0.5">
                  Confirm or decline within the 24-hour window so the school knows you're locked in.
                </div>
              </div>
            </div>
            <div className="space-y-2">
              {needsAction.map((ev) => (
                <EventCard
                  key={ev.id}
                  ev={ev}
                  highlight
                  onConfirm={() => confirm(ev.id)}
                  onDecline={() => decline(ev.id)}
                />
              ))}
            </div>
          </div>
        </section>
      )}

      <section className="mb-8">
        <div className="flex items-center justify-between mb-3">
          <h2 className="text-[13px] font-medium text-white tracking-tight">
            Upcoming events {upcoming.length > 0 && <span className="text-gray-500">({upcoming.length})</span>}
          </h2>
          <div className="inline-flex rounded-full bg-white/[0.04] border border-white/[0.08] p-0.5">
            <button type="button" onClick={() => setCalView('list')}
              className={`inline-flex items-center gap-1 px-2.5 py-1 rounded-full text-[11px] ${calView === 'list' ? 'bg-[#B0FC38] text-gray-900' : 'text-gray-400 hover:text-white'}`}>
              <List className="w-3 h-3" /> List
            </button>
            <button type="button" onClick={() => setCalView('calendar')}
              className={`inline-flex items-center gap-1 px-2.5 py-1 rounded-full text-[11px] ${calView === 'calendar' ? 'bg-[#B0FC38] text-gray-900' : 'text-gray-400 hover:text-white'}`}>
              <LayoutGrid className="w-3 h-3" /> Calendar
            </button>
          </div>
        </div>
        {loading ? (
          <div className="text-[12px] text-gray-500">Loading…</div>
        ) : calView === 'calendar' ? (
          <div className="rounded-xl border border-white/[0.06] bg-white/[0.02] p-3">
            <CalendarView
              events={upcoming.map((e) => ({
                id: e.id, title: e.eventTitle, start: e.scheduledStartAt, end: e.scheduledEndAt, status: e.status, raw: e,
              }))}
              onSelectEvent={(ev) => setDetailTarget(ev.raw)}
            />
          </div>
        ) : upcoming.length === 0 ? (
          <div className="rounded-xl border border-white/[0.06] bg-white/[0.02] p-5 text-[12px] text-gray-500">
            Nothing on the calendar. New bookings will appear here as soon as schools assign you.
          </div>
        ) : (
          <div className="space-y-5">
            {upcomingByDay.map(({ key, label, events }) => (
              <div key={key}>
                <div className="text-[10px] uppercase tracking-wider text-gray-500 mb-2">{label}</div>
                <div className="space-y-2">
                  {events.map((ev) => (
                <EventCard
                  key={ev.id}
                  ev={ev}
                  onSchedulePremeeting={() => setPremeetingTarget(ev)}
                  onCancelPremeeting={() => cancelPremeeting(ev.id)}
                  onRequestReschedule={() => setRescheduleTarget(ev)}
                  onCompleteOrDeliver={() => setCompleteTarget(ev)}
                />
              ))}
                </div>
              </div>
            ))}
          </div>
        )}
      </section>

      {decideTarget && (
        <RescheduleDecisionDialog
          request={decideTarget}
          apiBase="/api/media/me/reschedule-requests"
          onClose={() => setDecideTarget(null)}
          onDecided={() => { setDecideTarget(null); load(); }}
        />
      )}

      {premeetingTarget && (
        <PreMeetingScheduler
          event={premeetingTarget}
          oauthConnected={oauthConnected}
          onClose={() => setPremeetingTarget(null)}
          onScheduled={() => { setPremeetingTarget(null); load(); }}
        />
      )}

      {rescheduleTarget && (
        <RescheduleEventDialog
          event={rescheduleTarget}
          submitPath={`/api/media/me/events/${rescheduleTarget.id}/request-reschedule`}
          showFee={false}
          onClose={() => setRescheduleTarget(null)}
          onSubmitted={() => { setRescheduleTarget(null); load(); }}
        />
      )}

      {completeTarget && (
        <CompleteAndDeliverForm
          event={completeTarget}
          onClose={() => setCompleteTarget(null)}
          onSaved={() => { setCompleteTarget(null); load(); }}
        />
      )}

      {detailTarget && (
        <MediaEventDetail event={detailTarget} onClose={() => setDetailTarget(null)} />
      )}

      {!loading && projectShoots.length > 0 && (
        <section className="mb-8" data-testid="project-shoots">
          <h2 className="text-[13px] font-medium text-white tracking-tight mb-1 inline-flex items-center gap-1.5"><Camera className="w-3.5 h-3.5 text-gray-400" aria-hidden="true" /> Website shoots</h2>
          <p className="text-[11.5px] text-gray-500 mb-3">School sites in production that need photos or video. These holds are placed by Ops from the project board and move with it.</p>
          <div className="space-y-2">
            {projectShoots.map((s) => (
              <div key={s.blockId} className="rounded-xl border border-white/[0.06] bg-white/[0.02] px-4 py-3" data-testid={`project-shoot-${s.projectId}`}>
                <div className="flex items-center gap-2 flex-wrap">
                  <span className="text-[13px] text-white font-medium truncate">{s.accountName || s.projectName}</span>
                  <span className="text-[9px] uppercase tracking-wider px-1.5 py-0.5 rounded-full bg-violet-500/10 text-violet-300 border border-violet-500/20">Website shoot</span>
                  {s.mediaSubtypes.map((m) => <span key={m} className="text-[9px] uppercase tracking-wider px-1.5 py-0.5 rounded-full bg-white/[0.05] text-gray-400">{String(m).replace(/_/g, ' ')}</span>)}
                </div>
                <div className="text-[11.5px] text-gray-400 mt-1 flex items-center gap-1.5 flex-wrap"><Clock className="w-3 h-3" aria-hidden="true" /> {formatMediaDateRange(s.startAt, s.endAt)}</div>
                {s.schoolAddress && <div className="text-[11.5px] text-gray-500 mt-0.5 flex items-center gap-1.5"><MapPin className="w-3 h-3" aria-hidden="true" /> {s.schoolAddress}</div>}
                {s.schoolContact && <div className="text-[11.5px] text-gray-500 mt-0.5">Meet {typeof s.schoolContact === 'string' ? s.schoolContact : [s.schoolContact.name, s.schoolContact.phone, s.schoolContact.email].filter(Boolean).join(' · ')}</div>}
                {s.notes && <div className="text-[11.5px] text-gray-500 mt-1">{s.notes}</div>}
              </div>
            ))}
          </div>
        </section>
      )}

      {!loading && myTasks.length > 0 && (
        <section className="mb-8" data-testid="my-tasks">
          <h2 className="text-[13px] font-medium text-white tracking-tight mb-3 inline-flex items-center gap-1.5"><CheckSquare className="w-3.5 h-3.5 text-gray-400" aria-hidden="true" /> My tasks</h2>
          <ul className="space-y-1.5 list-none m-0 p-0">
            {myTasks.map((t) => (
              <li key={t.id} className="rounded-lg border border-white/[0.05] bg-white/[0.02] px-3 py-2.5 flex items-center gap-3" data-testid={`my-task-${t.id}`}>
                <div className="min-w-0 flex-1">
                  <div className="text-[13px] text-white truncate">{t.title}</div>
                  <div className="text-[11px] text-gray-500 mt-0.5">{[t.accountName, t.dueDate ? `due ${t.dueDate}` : null, t.priority !== 'normal' ? t.priority : null, t.createdByName ? `from ${t.createdByName}` : null].filter(Boolean).join(' · ')}</div>
                  {t.description && <div className="text-[11.5px] text-gray-400 mt-1">{t.description}</div>}
                </div>
                <button type="button" onClick={() => completeTask(t.id)} className="inline-flex items-center gap-1 px-2.5 py-1.5 rounded-lg text-[12px] text-gray-300 border border-white/[0.1] hover:text-white hover:border-[#B0FC38]/40 shrink-0" data-testid={`my-task-done-${t.id}`}>
                  <Check className="w-3.5 h-3.5" aria-hidden="true" /> Done
                </button>
              </li>
            ))}
          </ul>
        </section>
      )}

      {recent.length > 0 && (
        <section>
          <h2 className="text-[13px] font-medium text-white tracking-tight mb-3">Recently delivered</h2>
          <div className="space-y-1.5">
            {recent.map((ev) => (
              <div key={ev.id} className="rounded-lg border border-white/[0.04] bg-white/[0.02] px-3 py-2.5">
                <div className="flex items-center gap-2 flex-wrap">
                  <span className="text-[13px] text-white truncate">{ev.eventTitle}</span>
                  <span className={`text-[9px] uppercase tracking-wider px-1.5 py-0.5 rounded-full ${eventStatusBadgeClass(ev.status)}`}>
                    {eventStatusLabel(ev.status)}
                  </span>
                </div>
                <div className="text-[11px] text-gray-500 mt-0.5">
                  {ev.accountName} · {formatMediaDateRange(ev.scheduledStartAt, ev.scheduledEndAt)}
                </div>
              </div>
            ))}
          </div>
        </section>
      )}
    </div>
  );
}

function EventCard({ ev, highlight = false, confirmLabel = 'Confirm', onConfirm = null, onDecline = null, onSchedulePremeeting = null, onCancelPremeeting = null, onRequestReschedule = null, onCompleteOrDeliver = null }) {
  const isPending = ev.status === 'pending';
  return (
    <div
      className={`rounded-xl border p-4 ${
        highlight ? 'border-amber-400/40 bg-amber-500/[0.04]' : 'border-white/[0.06] bg-white/[0.02]'
      }`}
    >
      <div className="flex items-start justify-between gap-3 flex-wrap mb-2">
        <div className="min-w-0">
          <div className="flex items-center gap-2 flex-wrap">
            <span className="text-sm text-white font-medium truncate">{ev.eventTitle}</span>
            <span className={`text-[9px] uppercase tracking-wider px-1.5 py-0.5 rounded-full ${eventStatusBadgeClass(ev.status)}`}>
              {eventStatusLabel(ev.status)}
            </span>
            {ev.noticeTier !== 'standard' && (
              <span className={`text-[9px] uppercase tracking-wider px-1.5 py-0.5 rounded-full ${noticeTierBadgeClass(ev.noticeTier)}`}>
                {noticeTierLabel(ev.noticeTier)}
              </span>
            )}
            {ev.turnaround === 'rush' && (
              <span className="text-[9px] uppercase tracking-wider px-1.5 py-0.5 rounded-full bg-orange-500/[0.1] border border-orange-400/30 text-orange-300">
                Rush turnaround
              </span>
            )}
          </div>
          <div className="text-[12px] text-gray-400 mt-0.5">{ev.accountName}</div>
        </div>
        {isPending && ev.confirmationDeadlineAt && (
          <div className="text-[10px] text-amber-300 inline-flex items-center gap-1 shrink-0">
            <Clock className="w-3 h-3" />
            Confirm by {formatRelativeDeadline(ev.confirmationDeadlineAt)}
          </div>
        )}
      </div>

      <div className="grid grid-cols-1 md:grid-cols-2 gap-x-4 gap-y-1 text-[11px] text-gray-400">
        <div className="inline-flex items-center gap-1.5">
          <CalendarIcon className="w-3 h-3" />
          {formatMediaDateRange(ev.scheduledStartAt, ev.scheduledEndAt)}
        </div>
        <div className="inline-flex items-center gap-1.5 truncate" title={ev.locationAddress}>
          <MapPin className="w-3 h-3 shrink-0" />
          <span className="truncate">{ev.locationVenueName || ev.locationAddress}</span>
        </div>
      </div>

      {isPending && (onConfirm || onDecline) && (
        <div className="mt-3 flex items-center gap-2">
          {onConfirm && (
            <button
              type="button"
              onClick={onConfirm}
              className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-lg bg-[#B0FC38] text-gray-900 text-[12px] font-semibold hover:bg-[#9BE22E]"
            >
              <Check className="w-3.5 h-3.5" />
              {confirmLabel}
            </button>
          )}
          {onDecline && (
            <button
              type="button"
              onClick={onDecline}
              className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-lg border border-white/[0.1] text-gray-300 text-[12px] hover:text-white hover:border-white/[0.2]"
            >
              <X className="w-3.5 h-3.5" />
              Decline
            </button>
          )}
        </div>
      )}

      {/* Complete / Deliver actions (MP.7) — split by current status:
          booked → "Mark shoot complete" (sets actual_end + flips to editing)
          editing → "Update delivery" (Drive URL + 3 checkboxes)
          The single CompleteAndDeliverForm component handles both via
          status-aware rendering. */}
      {(ev.status === 'booked' || ev.status === 'editing') && onCompleteOrDeliver && (
        <div className="mt-3 pt-3 border-t border-white/[0.04] flex items-center justify-between gap-2 flex-wrap">
          <button type="button" onClick={onCompleteOrDeliver}
            className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-lg bg-[#B0FC38] text-gray-900 text-[12px] font-semibold hover:bg-[#9BE22E]">
            {ev.status === 'booked' ? 'Mark shoot complete' : 'Update delivery'}
          </button>
          {ev.status === 'editing' && ev.driveFolderUrl && (
            <span className="text-[11px] text-gray-500 truncate" title={ev.driveFolderUrl}>
              Drive folder set · finish all 3 confirmations to deliver
            </span>
          )}
        </div>
      )}

      {/* Pre-event meeting + reschedule controls — booked/shooting/editing
          (not pending, since the shooter hasn't confirmed yet). */}
      {!isPending && ev.status !== 'cancelled' && ev.status !== 'delivered' && (
        <div className="mt-3 pt-3 border-t border-white/[0.04] space-y-2">
          {ev.premeetingMeetUrl ? (
            <div className="flex items-center justify-between gap-2 flex-wrap">
              <a href={ev.premeetingMeetUrl} target="_blank" rel="noopener noreferrer"
                className="inline-flex items-center gap-1.5 text-[12px] text-[#B0FC38] hover:text-white">
                <Video className="w-3.5 h-3.5" />
                Join pre-event meeting
              </a>
              <div className="flex items-center gap-3 flex-wrap">
                {ev.premeetingScheduledAt && (
                  <span className="text-[11px] text-gray-500">
                    {new Date(ev.premeetingScheduledAt).toLocaleString('en-US', { dateStyle: 'medium', timeStyle: 'short' })}
                  </span>
                )}
                {onSchedulePremeeting && (
                  <button type="button" onClick={onSchedulePremeeting} className="text-[11px] text-gray-400 hover:text-white">
                    Reschedule
                  </button>
                )}
                {onCancelPremeeting && (
                  <button type="button" onClick={onCancelPremeeting} className="text-[11px] text-red-300/80 hover:text-red-300">
                    Cancel meeting
                  </button>
                )}
              </div>
            </div>
          ) : onSchedulePremeeting && (
            <button type="button" onClick={onSchedulePremeeting}
              className="inline-flex items-center gap-1.5 px-2.5 py-1 rounded-md border border-[#B0FC38]/30 bg-[#B0FC38]/[0.06] text-[11px] text-[#B0FC38] hover:bg-[#B0FC38]/[0.12]">
              <Video className="w-3 h-3" />
              Schedule pre-event meeting
            </button>
          )}

          {onRequestReschedule && (
            <button type="button" onClick={onRequestReschedule}
              className="inline-flex items-center gap-1.5 text-[11px] text-gray-400 hover:text-white">
              <CalendarClock className="w-3 h-3" />
              Request reschedule
            </button>
          )}
        </div>
      )}
    </div>
  );
}

function timeGreeting() {
  const hour = new Date().getHours();
  return hour < 12 ? 'Good morning' : hour < 18 ? 'Good afternoon' : 'Good evening';
}

function firstNameOf(name) {
  return (name || '').split(/[\s.]/)[0] || 'there';
}

function formatRelativeDeadline(iso) {
  if (!iso) return '';
  const ms = Date.parse(iso) - Date.now();
  if (ms <= 0) return 'overdue';
  const hours = Math.round(ms / 3_600_000);
  if (hours < 1) return 'under an hour';
  if (hours === 1) return '1 hour';
  return `${hours} hours`;
}

function groupByLocalDay(events) {
  const groups = new Map();
  for (const ev of events) {
    const d = new Date(ev.scheduledStartAt);
    if (Number.isNaN(d.valueOf())) continue;
    // NYC-local YYYY-MM-DD key
    const key = d.toLocaleDateString('en-CA', { timeZone: 'America/New_York' });
    if (!groups.has(key)) {
      const label = d.toLocaleDateString('en-US', {
        weekday: 'long', month: 'long', day: 'numeric',
        timeZone: 'America/New_York',
      });
      groups.set(key, { key, label, events: [] });
    }
    groups.get(key).events.push(ev);
  }
  return Array.from(groups.values());
}
